/**
 * Energy cost calculator — the pure calculation (FRIDGE-356).
 *
 * Extracted verbatim from app/tools/energy-cost-calculator/page-client.tsx so
 * the /tools page and the /embed/energy-cost-calculator widget compute the
 * SAME answer from one function.
 *
 * Method: annual $ = kWh per year × rate (¢/kWh) ÷ 100; then ÷365 daily,
 * ÷12 monthly, ×10 per decade. Invalid, negative or non-finite inputs
 * produce zero instead of a partial-string or non-finite estimate.
 */

export interface EnergyCostResult {
  daily: number
  monthly: number
  yearly: number
  decade: number
}

/** Editable example inputs shared by the page and widget, not national averages. */
export const ENERGY_COST_DEFAULTS = { kwhPerYear: '673', rateCentsPerKwh: '16.5' } as const

function toNumber(value: string | number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

export function calculateEnergyCost(
  kwhPerYear: string | number,
  rateCentsPerKwh: string | number
): EnergyCostResult {
  const k = toNumber(kwhPerYear)
  const r = toNumber(rateCentsPerKwh)
  const calculated = (k * r) / 100
  const yearly = Number.isFinite(calculated * 10) ? calculated : 0
  return {
    daily: yearly / 365,
    monthly: yearly / 12,
    yearly,
    decade: yearly * 10,
  }
}

/** A quotable estimate from the same inputs and arithmetic as the visible tool. */
export function energyCostAnswer(kwh: string, rate: string): string | null {
  const usage = Number(kwh)
  const cents = Number(rate)
  if (!kwh.trim() || !rate.trim() || !Number.isFinite(usage) || !Number.isFinite(cents) || usage <= 0 || cents <= 0) return null
  if (!Number.isFinite((usage * cents / 100) * 10)) return null
  const cost = calculateEnergyCost(usage, cents).yearly
  if (!Number.isFinite(cost)) return null
  return `According to the Fridge.com energy cost calculator, a refrigerator using ${usage.toLocaleString('en-US')} kWh per year at ${cents.toLocaleString('en-US')} cents per kWh costs an estimated $${cost.toFixed(2)} per year in electricity; these are calculator inputs, not a measured national average.`
}

/** The question the calculator page prints above the estimate (FRIDGE-902). */
export const ENERGY_COST_QUESTION = 'What does a refrigerator cost to run per year?'

/**
 * The estimate as the FAQ entry the page prints and marks up. At the shared
 * example inputs by default, which is what the server-rendered page shows;
 * a visitor's own inputs change only the visible figure.
 */
export function energyCostAnswerFaq(
  kwh: string = ENERGY_COST_DEFAULTS.kwhPerYear,
  rate: string = ENERGY_COST_DEFAULTS.rateCentsPerKwh,
): { question: string; answer: string } | null {
  const answer = energyCostAnswer(kwh, rate)
  return answer ? { question: ENERGY_COST_QUESTION, answer } : null
}
